import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GlassIconButton } from 'dashboard/components/common';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

/** Quick "what does this jurisdiction charge?" calculator for the owner. */
export default function TaxPreviewDialog({ open, onClose, brandId }) {
  const [form, setForm] = useState({ country: 'CA', region: 'ON', tax_code: '*', subtotal: 100 });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = () => {
    setLoading(true);
    API.previewTaxes({ brand_id: brandId, ...form, subtotal: Number(form.subtotal) })
      .then(setPreview)
      .catch((err) => toast.error(err?.message || 'Failed'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open) setPreview(null); }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pr: '48px' }}>
        Preview a sale
        <GlassIconButton label='Close' onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size='small'>
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing='10px'>
          <Stack direction='row' spacing='8px'>
            <TextField
              size='small'
              label='Country'
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase().slice(0, 2) })}
              fullWidth
            />
            <TextField
              size='small'
              label='Region'
              value={form.region}
              onChange={(e) => setForm({ ...form, region: e.target.value.toUpperCase().slice(0, 6) || '*' })}
              fullWidth
            />
          </Stack>
          <Stack direction='row' spacing='8px'>
            <TextField
              size='small'
              type='number'
              label='Subtotal'
              value={form.subtotal}
              onChange={(e) => setForm({ ...form, subtotal: e.target.value })}
              fullWidth
            />
            <TextField
              size='small'
              label='Product tax code'
              value={form.tax_code}
              onChange={(e) => setForm({ ...form, tax_code: e.target.value || '*' })}
              fullWidth
            />
          </Stack>
          <Button variant='contained' onClick={run} disabled={loading}>
            {loading ? 'Calculating…' : 'Calculate'}
          </Button>
          {preview && (
            <Box mt='10px' p='12px' bgcolor='action.hover' borderRadius='10px'>
              <Typography variant='body2' fontWeight='700' mb='8px'>Result</Typography>
              <Typography variant='body2'>
                Subtotal (excl. tax): <strong>{preview.subtotal_excl_tax.toFixed(2)}</strong>
              </Typography>
              {(preview.tax_lines || []).map((tl, i) => (
                <Typography variant='body2' key={i}>
                  {tl.name} ({(tl.rate_basis_points / 100).toFixed(2)}%
                  {tl.is_inclusive ? ' incl' : ''}{tl.is_compound ? ' compound' : ''}):{' '}
                  <strong>{tl.amount.toFixed(2)}</strong>
                </Typography>
              ))}
              <Divider sx={{ my: '8px' }} />
              <Typography variant='body2'>Tax total: <strong>{preview.tax_total.toFixed(2)}</strong></Typography>
              <Typography variant='body2'>Customer pays: <strong>{preview.line_total.toFixed(2)}</strong></Typography>
              <Typography variant='caption' color='text.secondary' mt='6px' display='block'>
                {preview.rules?.length || 0} rule(s) matched.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
